import { EMPTY_STR } from '@/utils/constants/common';

/**
 * Get the tracking type from the event info
 * Temporary solution to resolve tracking type from link name in eventInfo
 * @param eventInfo - The event info
 * @returns The tracking type
 */
export const getTrackingType = (eventInfo: Record<string, unknown>, text: string): string => {
    const { linkName, link_name } = eventInfo;

    // linkName / link_name are caller-supplied "fully-formed" tracking types.
    // Return them verbatim so composite values (e.g. `plan_table_cta:trackX`)
    // are preserved. Validity of the prefix is the caller's responsibility.
    if (linkName) {
        return String(linkName);
    }

    if (link_name) {
        return String(link_name);
    }

    if (text) {
        // The text-fallback branch OWNS the `track` prefix.
        // 1) Strip separator characters (slash, underscore, hyphen, spaces) so the
        //    type collapses to a single token, per the Orion naming convention
        //    (e.g. `sign_out` → `signout`, never `sign_out`). The class must list `_`
        //    and `-` individually — a `-_` sequence only matches the literal pair and
        //    leaves standalone underscores intact.
        const cleaned = text.replaceAll(/[/_ -]+/g, '');
        // 2) Strip any leading `track` prefixes so we can't double up when callers
        //    pass a value that already includes the prefix.
        const stripped = cleaned.replace(/^(track)+/i, '');
        return `track${stripped}`;
    }

    return EMPTY_STR;
};

/**
 * Clean the event info to remove the linkName and link_name keys
 * @param eventInfo - The event info
 * @returns The cleaned event info
 */
export const cleanEventInfo = (eventInfo: Record<string, unknown>) => {
    const { linkName, link_name } = eventInfo;
    const cleanedEventInfo = { ...eventInfo };

    if (linkName) {
        cleanedEventInfo.linkName = linkName;
        delete cleanedEventInfo.link_name;
    }
    if (link_name) {
        cleanedEventInfo.link_name = link_name;
        delete cleanedEventInfo.linkName;
    }
    return cleanedEventInfo;
};
